import { useEffect, useRef } from 'react'

import type {
  AssignedWorkflow,
  PendingWorkflow,
  WorkflowAction,
} from '../../../shared/hooks/useIncidentWorkflow'

import { Box } from '@hivewatch/dark'

import { IncidentWorkflowStatus } from '@/graphql/generated/schemas'

import { WorkflowCompleteState } from './WorkflowCompleteState'
import { WorkflowInProgressState } from './WorkflowInProgressState'
import { WorkflowLoadingSpinner } from './WorkflowLoadingSpinner'
import { WorkflowPendingState } from './WorkflowPendingState'

interface WorkflowContentIProps {
  assignedWorkflow: AssignedWorkflow | null
  currentStepId: string | null
  incidentId: string | null
  isActiveOwner: boolean
  pendingWorkflow: PendingWorkflow | null
  pendingWorkflowFull: AssignedWorkflow | null
  workflowActions: WorkflowAction[]
  workflowStatus: IncidentWorkflowStatus | null
}

interface WorkflowDrawerBodyIProps extends WorkflowContentIProps {
  isLoadingWorkflow: boolean
}

const WorkflowContent = ({
  assignedWorkflow,
  currentStepId,
  incidentId,
  isActiveOwner,
  pendingWorkflow,
  pendingWorkflowFull,
  workflowActions,
  workflowStatus,
}: WorkflowContentIProps): JSX.Element | null => {
  if (workflowStatus === IncidentWorkflowStatus.Complete) {
    if (currentStepId !== null) {
      return <WorkflowLoadingSpinner />
    }

    return (
      <WorkflowCompleteState
        assignedWorkflow={assignedWorkflow}
        workflowActions={workflowActions}
      />
    )
  }

  if (workflowStatus === IncidentWorkflowStatus.InProgress) {
    if (currentStepId === null) {
      return <WorkflowLoadingSpinner />
    }

    return (
      <WorkflowInProgressState
        assignedWorkflow={assignedWorkflow}
        currentStepId={currentStepId}
        incidentId={incidentId}
        isActiveOwner={isActiveOwner}
        workflowActions={workflowActions}
      />
    )
  }

  if (pendingWorkflow) {
    return (
      <WorkflowPendingState
        pendingWorkflow={pendingWorkflow}
        pendingWorkflowFull={pendingWorkflowFull}
      />
    )
  }

  return null
}

export const WorkflowDrawerBody = ({
  assignedWorkflow,
  currentStepId,
  incidentId,
  isActiveOwner,
  isLoadingWorkflow,
  pendingWorkflow,
  pendingWorkflowFull,
  workflowActions,
  workflowStatus,
}: WorkflowDrawerBodyIProps): JSX.Element => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const prevWorkflowStatusRef = useRef<IncidentWorkflowStatus | null>(null)

  const hasDataForSelectedWorkflowState =
    workflowStatus === IncidentWorkflowStatus.Complete
      ? workflowActions.length > 0
      : workflowStatus === IncidentWorkflowStatus.InProgress
      ? Boolean(assignedWorkflow)
      : Boolean(pendingWorkflowFull) || Boolean(pendingWorkflow)
  const shouldShowLoadingSpinner =
    isLoadingWorkflow && !hasDataForSelectedWorkflowState

  // Scroll to the bottom so the current question is always visible while
  // in progress. When transitioning to Complete, reset to the top so operators
  // see the full step history from the beginning.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const prevStatus = prevWorkflowStatusRef.current
    prevWorkflowStatusRef.current = workflowStatus

    if (workflowStatus === IncidentWorkflowStatus.Complete) {
      if (prevStatus === IncidentWorkflowStatus.InProgress) {
        el.scrollTop = 0
      }
      return
    }

    if (workflowStatus !== IncidentWorkflowStatus.InProgress) return

    if (typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
    } else {
      el.scrollTop = el.scrollHeight
    }
  }, [workflowActions.length, currentStepId, workflowStatus])

  return (
    <Box flex={1} overflow='auto' p='24px' ref={scrollRef}>
      {shouldShowLoadingSpinner ? (
        <WorkflowLoadingSpinner />
      ) : (
        <WorkflowContent
          assignedWorkflow={assignedWorkflow}
          currentStepId={currentStepId}
          incidentId={incidentId}
          isActiveOwner={isActiveOwner}
          pendingWorkflow={pendingWorkflow}
          pendingWorkflowFull={pendingWorkflowFull}
          workflowActions={workflowActions}
          workflowStatus={workflowStatus}
        />
      )}
    </Box>
  )
}
